/**
 * The Workshop sidebar (07 Workshop, Workshop / Sidebar): the title and its
 * fold, New chat, Search (⌘K), Creos, then Projects with its sort and +, each project
 * a folder that opens into its chats. Every chat lives in a project (Emad,
 * 3 Oct), so there is no loose Chats group.
 *
 * A project row opens the project's home; its chevron only shows or hides its
 * chats. Hovering a project or a chat shows its ⋯. Rename turns the chat's row
 * into a field (Return saves, Escape cancels). On a phone the same sidebar
 * fills the screen, with Close in place of the fold.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type TransitionEvent } from "react";
import { Button, Menu, MenuItem, MenuTrigger, Popover, type Key } from "react-aria-components";
import {
  ArrowDownUp,
  Check,
  ChevronRight,
  Folder,
  FolderOpen,
  LogOut,
  Menu as MenuIcon,
  PanelLeftClose,
  Plus,
  Search,
  SquarePen,
  Zap,
  Users,
  X,
} from "lucide-react";
import type { WorkshopSurface } from "./workshopDesk";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import { useAuthStore } from "@/stores/authStore";
import type { WorkshopProject, WorkshopSession } from "@/types/workshop.type";
import { useWorkshopProjects } from "../api/useWorkshopProjects";
import { useWorkshopSessions } from "../api/useWorkshopSessions";
import { useMoveSession } from "../api/useWorkshopMove";
import { PROJECT_SORTS, sortProjects, useProjectSort } from "../workshopSort";
import ShareThreadModal from "./ShareThreadModal";
import LeaveChatDialog from "./LeaveChatDialog";
import WorkshopChatMenu, { type ChatMenuAction } from "./WorkshopChatMenu";
import WorkshopDialog from "./WorkshopDialog";
import WorkshopProjectMenu from "./WorkshopProjectMenu";
import { useProjectActions } from "./WorkshopProjectActions";
import { useWorkshopDesk } from "./workshopDesk";
import { isArchived } from "./WorkshopParts";
import { useWorkshopUndo } from "./WorkshopUndo";

/** The key a held leave hides a chat under. */
export const heldThreadKey = (threadId: string) => `thread:${threadId}`;

export function threadTitle(session: Pick<WorkshopSession, "title">): string {
  return session.title.trim() || "New chat";
}

const THREAD_TITLE_MAX = 120;
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

type Props = {
  /** "desk" beside the page; "sheet" fills a phone's screen. */
  variant: "desk" | "sheet";
  /** The page the main area shows, for the row it lights. */
  surface: WorkshopSurface;
  onFold?: () => void;
  onClose?: () => void;
  /** Phone: the app’s own navigation (the lane’s menu opens this sidebar instead). */
  onOpenAppNav?: (trigger: HTMLElement) => void;
};

export default function WorkshopSidebar({ variant, surface, onFold, onClose, onOpenAppNav }: Props) {
  const desk = useWorkshopDesk();
  const { projects, isLoading } = useWorkshopProjects(desk.brandId);
  const [sort, setSort] = useProjectSort();
  const active = sortProjects(projects.filter((p) => !isArchived(p)), sort);
  const portal = useCoShellPortal();
  const isSheet = variant === "sheet";

  // The open project shows its chats; others open on their chevron. Which are
  // open is kept for this visit only.
  const [openIds, setOpenIds] = useState<string[]>(() => {
    const first = surface === "home" ? desk.pickedProjectId : desk.activeProjectId;
    return first ? [first] : [];
  });
  // The open project, or the one a new chat will live in, shows its chats.
  const focusId = surface === "home" ? desk.pickedProjectId : desk.activeProjectId;
  const [seenFocus, setSeenFocus] = useState(focusId);
  if (seenFocus !== focusId) {
    setSeenFocus(focusId);
    if (focusId && !openIds.includes(focusId)) setOpenIds([...openIds, focusId]);
  }
  const toggle = (id: string) =>
    setOpenIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const { run, dialogs } = useProjectActions({
    brandId: desk.brandId,
    onProjectDeleted: (id) => {
      if (id === desk.activeProjectId) desk.newChat();
    },
  });

  const go = (fn: () => void) => () => {
    fn();
    onClose?.();
  };

  return (
    <nav className="ws-side" data-variant={variant} aria-label="Workshop">
      <div className="ws-side-top">
        {isSheet && onOpenAppNav ? (
          <button
            type="button"
            className="ws-side-icon ws-side-appnav"
            aria-label="Open navigation"
            onClick={(event) => onOpenAppNav(event.currentTarget)}
          >
            <MenuIcon aria-hidden="true" />
          </button>
        ) : null}
        <h2 className="ws-side-title">Workshop</h2>
        {isSheet ? (
          <button type="button" className="ws-side-icon" onClick={onClose} aria-label="Close">
            <X aria-hidden="true" />
          </button>
        ) : onFold ? (
          <button
            type="button"
            className="ws-side-icon"
            onClick={onFold}
            aria-label="Hide the sidebar"
            title="Hide the sidebar"
          >
            <PanelLeftClose aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <button
        type="button"
        className="ws-side-row"
        aria-current={surface === "home" ? "page" : undefined}
        onClick={go(() => desk.newChat())}
      >
        <SquarePen aria-hidden="true" />
        <span>New chat</span>
      </button>
      <button type="button" className="ws-side-row" onClick={go(desk.openSearch)}>
        <Search aria-hidden="true" />
        <span>Search</span>
        {isSheet ? null : <kbd className="ws-side-kbd">{IS_MAC ? "⌘K" : "Ctrl K"}</kbd>}
      </button>
      <button
        type="button"
        className="ws-side-row"
        aria-current={surface === "creos" ? "page" : undefined}
        onClick={go(desk.openCreos)}
      >
        <Zap aria-hidden="true" />
        <span>Creos</span>
      </button>

      <div className="ws-side-head">
        <button
          type="button"
          className="ws-side-head-label"
          aria-current={surface === "projects" ? "page" : undefined}
          onClick={go(desk.openProjects)}
        >
          Projects
        </button>
        <span className="ws-side-push" />
        <MenuTrigger>
          <Button className="ws-side-icon ws-side-icon--sm" aria-label="Sort projects">
            <ArrowDownUp aria-hidden="true" />
          </Button>
          <Popover
            placement="bottom start"
            offset={6}
            UNSTABLE_portalContainer={portal ?? undefined}
            className="ws-menu-pop ws-menu-pop--sort"
          >
            <Menu
              aria-label="Sort projects"
              className="ws-menu-list"
              selectionMode="single"
              selectedKeys={[sort]}
              onAction={(key: Key) => setSort(key as typeof sort)}
            >
              {PROJECT_SORTS.map((s) => (
                <MenuItem key={s.id} id={s.id} textValue={s.label} className="ws-menu-row">
                  {({ isSelected }) => (
                    <>
                      <span className="ws-menu-row-label">{s.label}</span>
                      {isSelected ? <Check className="ws-menu-row-check" aria-hidden="true" /> : null}
                    </>
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Popover>
        </MenuTrigger>
        <button
          type="button"
          className="ws-side-icon"
          aria-label="New project"
          title="New project"
          onClick={() => desk.openNewProject((id) => desk.openProject(id))}
        >
          <Plus aria-hidden="true" />
        </button>
      </div>

      <div className="ws-side-scroll">
        {isLoading ? null : (
          <ul className="ws-side-tree">
            {active.map((project) => (
              <ProjectBranch
                key={project.id}
                project={project}
                projects={active}
                open={openIds.includes(project.id)}
                current={surface === "project" && project.id === desk.activeProjectId}
                isSheet={isSheet}
                onToggle={() => toggle(project.id)}
                onOpen={go(() => {
                  if (!openIds.includes(project.id)) toggle(project.id);
                  desk.openProject(project.id);
                })}
                onProjectAction={(action) => run(action, project)}
                onNavigated={onClose}
              />
            ))}
          </ul>
        )}
      </div>
      {dialogs}
    </nav>
  );
}

function ProjectBranch({
  project,
  projects,
  open,
  current,
  isSheet,
  onToggle,
  onOpen,
  onProjectAction,
  onNavigated,
}: {
  project: WorkshopProject;
  projects: WorkshopProject[];
  open: boolean;
  current: boolean;
  isSheet: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onProjectAction: Parameters<typeof WorkshopProjectMenu>[0]["onAction"];
  onNavigated?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Keep the chats mounted through the close so the fold can shrink. A project
  // that is already open on arrival stays open; only a later toggle grows.
  const [showChats, setShowChats] = useState(open);
  const [chatsReady, setChatsReady] = useState(open);
  const [grown, setGrown] = useState(open);
  const markChatsReady = useCallback(() => setChatsReady(true), []);
  const FolderIcon = open ? FolderOpen : Folder;

  useLayoutEffect(() => {
    if (open) {
      setShowChats(true);
      return;
    }
    setGrown(false);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShowChats(false);
      setChatsReady(false);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !showChats || !chatsReady || grown) return;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setGrown(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [open, showChats, chatsReady, grown]);

  useEffect(() => {
    if (open || !showChats) return;
    const timeout = window.setTimeout(() => {
      setShowChats(false);
      setChatsReady(false);
    }, 480);
    return () => window.clearTimeout(timeout);
  }, [open, showChats]);

  const onFoldEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.propertyName !== "grid-template-rows") return;
    if (!open) {
      setShowChats(false);
      setChatsReady(false);
    }
  };

  return (
    <li>
      <div
        className="ws-side-project"
        data-current={current || undefined}
        data-open={open || undefined}
        data-menu={menuOpen || undefined}
      >
        <button
          type="button"
          className="ws-side-chev"
          aria-expanded={open}
          aria-label={open ? `Hide the chats in ${project.name}` : `Show the chats in ${project.name}`}
          onClick={onToggle}
        >
          <ChevronRight aria-hidden="true" />
        </button>
        <button
          type="button"
          className="ws-side-project-open"
          aria-current={current ? "page" : undefined}
          onClick={onOpen}
        >
          <FolderIcon className="ws-side-folder" aria-hidden="true" />
          <span className="ws-side-name">{project.name}</span>
        </button>
        <WorkshopProjectMenu
          project={project}
          canDelete={project.isOwner !== false}
          asSheet={isSheet}
          onAction={onProjectAction}
          className="ws-side-more"
          onOpenChange={setMenuOpen}
        />
      </div>
      {showChats ? (
        <div className="ws-side-fold" data-open={grown || undefined} onTransitionEnd={onFoldEnd}>
          <div className="ws-side-fold-inner">
            <ProjectChats
              project={project}
              projects={projects}
              isSheet={isSheet}
              onNavigated={onNavigated}
              onReady={markChatsReady}
            />
          </div>
        </div>
      ) : null}
    </li>
  );
}

function ProjectChats({
  project,
  projects,
  isSheet,
  onNavigated,
  onReady,
}: {
  project: WorkshopProject;
  projects: WorkshopProject[];
  isSheet: boolean;
  onNavigated?: () => void;
  /** Fired once the chat list can take height, so the fold grows into it. */
  onReady: () => void;
}) {
  const desk = useWorkshopDesk();
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const undo = useWorkshopUndo();
  const { sessions, isLoading, renameSession, deleteSession, removeParticipant, leaveOnUnload } =
    useWorkshopSessions(project.id);
  const move = useMoveSession(desk.brandId);
  const [pendingDelete, setPendingDelete] = useState<WorkshopSession | null>(null);
  const [sharing, setSharing] = useState<WorkshopSession | null>(null);
  const [leaving, setLeaving] = useState<WorkshopSession | null>(null);
  const chats = sessions.filter((s) => !undo.isHeld(heldThreadKey(s.id)));

  useLayoutEffect(() => {
    if (!isLoading) onReady();
  }, [isLoading, onReady]);

  /** The open chat is going: back to its project's home. */
  const moveOff = (session: WorkshopSession) => {
    if (session.id === desk.activeThreadId) desk.openProject(project.id);
  };

  const leave = (session: WorkshopSession) => {
    if (!viewerId) return;
    moveOff(session);
    undo.hold({
      id: heldThreadKey(session.id),
      icon: LogOut,
      title: `You left “${threadTitle(session)}”`,
      line: "Its owner can add you back.",
      commit: () => removeParticipant.mutate({ sessionId: session.id, userId: viewerId, leaving: true }),
      commitOnUnload: () => leaveOnUnload(session.id, viewerId),
    });
  };

  const moveTo = (session: WorkshopSession, toProjectId: string) => {
    const to = projects.find((p) => p.id === toProjectId);
    move.mutate(
      { session, toProjectId, toProjectName: to?.name ?? "the new project" },
      {
        // The open chat follows itself into its new project.
        onSuccess: () => {
          if (session.id === desk.activeThreadId) desk.openThread(toProjectId, session.id);
        },
      },
    );
  };

  const onAction = (session: WorkshopSession) => (action: ChatMenuAction) => {
    if (action === "rename") desk.startRename(session.id);
    else if (action === "share") setSharing(session);
    else if (action === "delete") setPendingDelete(session);
    else setLeaving(session);
  };

  if (isLoading) return null;

  return (
    <>
      <ul className="ws-side-chats" aria-label={`Chats in ${project.name}`}>
        {chats.map((session) => (
          <ChatRow
            key={session.id}
            session={session}
            projects={projects}
            current={session.id === desk.activeThreadId}
            renaming={desk.renamingThreadId === session.id}
            isSheet={isSheet}
            onOpen={() => {
              desk.openThread(project.id, session.id);
              onNavigated?.();
            }}
            onRename={(title) => {
              desk.endRename();
              if (title && title !== session.title) renameSession.mutate({ sessionId: session.id, title });
            }}
            onCancelRename={desk.endRename}
            onAction={onAction(session)}
            onMove={(toProjectId) => moveTo(session, toProjectId)}
            onMoveToNewProject={() => desk.openNewProject((id) => moveTo(session, id))}
          />
        ))}
      </ul>
      <WorkshopDialog
        isOpen={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Delete this chat?"
        description={
          pendingDelete
            ? `“${threadTitle(pendingDelete)}” and its messages go for everyone in it. The outputs it made stay in the project.`
            : undefined
        }
        consequence="This cannot be undone"
        cancelLabel="Keep chat"
        confirmLabel="Delete chat"
        confirmTone="danger"
        onConfirm={() => {
          if (!pendingDelete) return;
          const session = pendingDelete;
          setPendingDelete(null);
          moveOff(session);
          deleteSession.mutate(session.id);
        }}
      />
      <LeaveChatDialog
        session={leaving}
        onCancel={() => setLeaving(null)}
        onLeave={(session) => {
          setLeaving(null);
          leave(session);
        }}
      />
      {sharing ? (
        <ShareThreadModal
          projectId={project.id}
          session={sharing}
          isOpen
          onOpenChange={(open) => {
            if (!open) setSharing(null);
          }}
        />
      ) : null}
    </>
  );
}

function ChatRow({
  session,
  projects,
  current,
  renaming,
  isSheet,
  onOpen,
  onRename,
  onCancelRename,
  onAction,
  onMove,
  onMoveToNewProject,
}: {
  session: WorkshopSession;
  projects: WorkshopProject[];
  current: boolean;
  renaming: boolean;
  isSheet: boolean;
  onOpen: () => void;
  onRename: (title: string) => void;
  onCancelRename: () => void;
  onAction: (action: ChatMenuAction) => void;
  onMove: (projectId: string) => void;
  onMoveToNewProject: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const title = threadTitle(session);
  const owns = session.isOwner !== false;
  const shared = Boolean(session.sharedAt);

  if (renaming) {
    return (
      <li className="ws-side-chat ws-side-chat--rename">
        <RenameField initial={session.title} onSave={onRename} onCancel={onCancelRename} />
      </li>
    );
  }

  return (
    <li
      className="ws-side-chat"
      data-current={current || undefined}
      data-menu={menuOpen || undefined}
    >
      <button
        type="button"
        className="ws-side-chat-open"
        aria-current={current ? "page" : undefined}
        onClick={onOpen}
        onDoubleClick={owns && !isSheet ? () => onAction("rename") : undefined}
      >
        {shared ? <Users className="ws-side-shared" aria-label="Shared chat" /> : null}
        <span className="ws-side-chat-title">{title}</span>
      </button>
      <WorkshopChatMenu
        title={title}
        isOwner={owns}
        canEdit
        canMove={!shared}
        projectId={session.projectId}
        projects={projects}
        isPhone={isSheet}
        onAction={onAction}
        onMove={onMove}
        onMoveToNewProject={onMoveToNewProject}
        triggerClassName="ws-more ws-side-more"
        onOpenChange={setMenuOpen}
      />
    </li>
  );
}

/** The row as a field: Return saves, Escape cancels, leaving it saves. */
function RenameField({
  initial,
  onSave,
  onCancel,
}: {
  initial: string;
  onSave: (title: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (save: boolean) => {
    if (done.current) return;
    done.current = true;
    if (save) onSave(value.trim());
    else onCancel();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      finish(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      finish(false);
    }
  };
  return (
    <input
      ref={ref}
      className="ws-side-rename co-input-own-focus"
      value={value}
      maxLength={THREAD_TITLE_MAX}
      aria-label="Chat name"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={onKeyDown}
      onBlur={() => finish(true)}
    />
  );
}
